import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Package, Wrench, FileText, MessageSquare, ArrowRight, ArrowUp, Loader, TrendingUp, Image } from "lucide-react";
import { dashboardAPI } from "../../services/api";
import { useAuth } from "../../context/AuthContext";

export default function AdminDashboard() {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    dashboardAPI.getStats()
      .then(r => setStats(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const kpis = [
    { label: "Total Products", value: stats?.products || 0, icon: Package, path: "/admin/products", color: "brand" },
    { label: "Services", value: stats?.services || 0, icon: Wrench, path: "/admin/services", color: "gold" },
    { label: "Blog Posts", value: stats?.posts || 0, icon: FileText, path: "/admin/blog", color: "brand" },
    { label: "New Messages", value: stats?.newMessages || 0, icon: MessageSquare, path: "/admin/messages", color: "gold" },
    { label: "Gallery Items", value: stats?.gallery || 0, icon: Image, path: "/admin/gallery", color: "brand" },
    { label: "Total Messages", value: stats?.totalMessages || 0, icon: TrendingUp, path: "/admin/messages", color: "gold" },
  ];

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading font-bold text-gray-900 text-2xl">Welcome back, {user?.name?.split(" ")[0]} 👋</h1>
          <p className="text-gray-400 text-sm mt-0.5">Here's what's happening with Sava Fresh today.</p>
        </div>
        <Link to="/admin/products" className="btn-primary text-xs px-4 py-2">Add Product <ArrowRight className="w-3.5 h-3.5" /></Link>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-20"><Loader className="w-6 h-6 text-brand-primary animate-spin mr-2" /><span className="text-gray-400 text-sm">Loading...</span></div>
      ) : (
        <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
          {kpis.map(kpi => (
            <Link key={kpi.label} to={kpi.path}
              className="bg-white rounded-2xl p-5 shadow-card hover:shadow-hover transition-all duration-300 border border-gray-50 group block">
              <div className="flex items-center justify-between mb-3">
                <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${kpi.color === "gold" ? "bg-accent-goldLight" : "bg-brand-pale"}`}>
                  <kpi.icon className={`w-5 h-5 ${kpi.color === "gold" ? "text-accent-gold" : "text-brand-primary"}`} />
                </div>
                <ArrowUp className="w-3.5 h-3.5 text-gray-300 group-hover:text-brand-primary transition-colors" />
              </div>
              <div className="font-heading font-extrabold text-gray-900 text-3xl mb-0.5">{kpi.value}</div>
              <div className="text-gray-400 text-xs font-medium">{kpi.label}</div>
            </Link>
          ))}
        </div>
      )}

      {/* Recent Messages */}
      {stats?.recentMessages?.length > 0 && (
        <div className="bg-white rounded-2xl shadow-card overflow-hidden">
          <div className="p-5 border-b border-gray-100 flex items-center justify-between">
            <h3 className="font-heading font-bold text-gray-900">Recent Messages</h3>
            <Link to="/admin/messages" className="text-xs text-brand-primary font-semibold hover:underline flex items-center gap-1">View All <ArrowRight className="w-3 h-3" /></Link>
          </div>
          <div className="divide-y divide-gray-50">
            {stats.recentMessages.slice(0,5).map(msg => (
              <div key={msg.id} className="px-5 py-3.5 flex items-center gap-4 hover:bg-gray-50 transition-colors">
                <div className="w-8 h-8 bg-brand-pale rounded-full flex items-center justify-center text-brand-primary font-bold text-xs flex-shrink-0">{msg.name?.charAt(0)}</div>
                <div className="flex-1 min-w-0">
                  <div className="font-medium text-gray-900 text-sm truncate">{msg.name}</div>
                  <div className="text-xs text-gray-400 truncate">{msg.subject || msg.message?.slice(0,50)}</div>
                </div>
                <span className={`badge text-xs px-2 py-1 rounded-full ${msg.status === "NEW" ? "bg-orange-50 text-orange-600" : "bg-green-50 text-green-600"}`}>{msg.status === "NEW" ? "New" : "Read"}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Quick Actions */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {[
          { label: "Add Product", path: "/admin/products", icon: Package },
          { label: "New Blog Post", path: "/admin/blog", icon: FileText },
          { label: "Add Service", path: "/admin/services", icon: Wrench },
          { label: "Site Settings", path: "/admin/settings", icon: "⚙️" },
        ].map(item => (
          <Link key={item.label} to={item.path}
            className="flex items-center gap-2.5 p-4 bg-white rounded-2xl shadow-card hover:shadow-hover transition-all duration-300 border border-transparent hover:border-brand-muted">
            {typeof item.icon === "string" ? <span className="text-xl">{item.icon}</span> : <item.icon className="w-5 h-5 text-brand-primary" />}
            <span className="text-sm font-semibold text-gray-700">{item.label}</span>
          </Link>
        ))}
      </div>
    </div>
  );
}
